import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import { kExternalStorageAccountsPath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { ExternalStorageDisconnectPreview, ExternalStorageDisconnectPreviewSchema } from '@/settings/schemas/external-storage-schema'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

function formatErrorDetail(data: unknown): string {
  const responseData = data as { detail?: unknown } | undefined
  const detail = responseData?.detail ?? data
  if (typeof detail === 'string') return detail
  if (detail == null) return ''
  return JSON.stringify(detail)
}

export default async function getExternalStorageDisconnectPreview(accountId: string): Promise<ExternalStorageDisconnectPreview> {
  const sentryTransactionId = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kExternalStorageAccountsPath}/${encodeURIComponent(accountId)}/disconnect/preview`
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
      },
    })
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            accountId,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, detail || `Failed to load disconnect preview: ${response.status}`, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('External storage disconnect preview response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            accountId,
            parseError: formatUnknownError(parseError),
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid external storage disconnect preview response JSON')
    }

    const parsed = ExternalStorageDisconnectPreviewSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('External storage disconnect preview response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            accountId,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid external storage disconnect preview response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, { extra: { accountId } })
      })
    }
    throw error
  }
}
